import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faArrowLeft, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { EntitiesContext } from '../../../App';
import { useUpdateInventory } from '../../../hooks/useInventory';
import { useFindOrder, useGetOrderItems } from '../../../hooks/useOrder';

export default function SaleEditOrder() {
  const params = useParams();
  const navigate = useNavigate();
  const entities = useContext(EntitiesContext);
  const updateInventory = useUpdateInventory();
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState('');
  const [searchOrderId, setSearchOrderId] = useState('');

  const accounts = entities.state.get('account');
  const vendors = entities.state.get('account').filter(a => a.role === 'vendor');
  const order = entities.state.get('order').get(parseInt(params.id));
  const items = order ? entities.state.get('inventory').filter(i => i.order_id === order.id) : entities.state.get('inventory').filter(() => false);

  const getItems = useGetOrderItems();

  // Fetch the order when the component loads or when the order ID changes
  useFindOrder(parseInt(params.id));

  // Fetch order items when order is loaded
  useEffect(() => {
    if (order) {
      getItems.mutateAsync(order);
    }
  }, [order?.id]);

  const edit = item => {
    setEditing(item);
    setNote(item.note || ''); // Initialize note from the item being edited
  }

  const isVendor = id => {
    return accounts.get(id)?.role === 'vendor';
  }

  const cancel = () => {
    setEditing(null);
    setNote(''); // Clear note when canceling
  }

  const save = () => {
    updateInventory.mutate({ ...editing.toJS(), ...{ note: note } });
    setEditing(null);
    setNote(''); // Clear note after saving
  }

  const row = item => {
    if (editing && editing.id === item.id &&  isVendor(editing?.account_id)) {
      return <tr key={item.id}>
        <td className="select">
          <select name="account_id" value={editing.account_id} onChange={e => setEditing(editing.set(e.target.name, parseInt(e.target.value)))}>
            {vendors.valueSeq().map(v => <option key={v.id} value={v.id}>
              {v.vendor_name}
            </option>)}
          </select>
        </td>
        <td><input type="text" className="input" name="name" value={editing.name} onChange={e => setEditing(editing.set(e.target.name, e.target.value))} /></td>
        <td><input type="text" className="input" name="price" value={editing.price} onChange={e => setEditing(editing.set(e.target.name, parseFloat(e.target.value)))} /></td>
        <td><input type="text" className="input" name="note" value={note} onChange={e => setNote(e.target.value)} /></td>
        <td>
          <button className="button is-small is-danger" onClick={cancel} title="Cancel">
            <span className="icon">
              <FontAwesomeIcon icon={faXmark} />
            </span>
          </button>
          {' '}
          <button className="button is-small is-success" onClick={save} title="Save" disabled={updateInventory.isLoading}>
            <span className="icon">
              <FontAwesomeIcon icon={faCheck} />
            </span>
          </button>
        </td>
      </tr>
    } else {
      return <tr key={item.id}>
        <td>{isVendor(item.account_id) ? accounts.get(item.account_id).vendor_name : `${accounts.get(item.account_id).first_name} ${accounts.get(item.account_id).last_name}`}</td>
        <td>{item.name}</td>
        <td>${item.price}</td>
        <td>{isVendor(item.account_id) && <a onClick={() => edit(item)}>edit</a>}</td>
      </tr>;
    }
  }

  if (items.count() < 1) return <div>loading</div>

  return (
    <div>
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <Link to="/admin/sales/orders" className="button is-small is-light">
              <span className="icon">
                <FontAwesomeIcon icon={faArrowLeft} />
              </span>
              <span>Back to Orders</span>
            </Link>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <div className="title is-4 mb-0">
              Edit Order {order.id}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="columns">
          <div className="column is-half">
            <strong>Cashier:</strong> {accounts.get(order.account_id)?.first_name} {accounts.get(order.account_id)?.last_name}
          </div>
          <div className="column is-half">
            <div className="field has-addons">
              <div className="control">
                <input
                  type="number"
                  className="input"
                  placeholder="Find another order..."
                  value={searchOrderId}
                  onChange={e => setSearchOrderId(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && searchOrderId) {
                      navigate(`/admin/sales/order/${searchOrderId}`);
                      setSearchOrderId('');
                    }
                  }}
                />
              </div>
              <div className="control">
                <button
                  className="button is-primary"
                  onClick={() => {
                    if (searchOrderId) {
                      navigate(`/admin/sales/order/${searchOrderId}`);
                      setSearchOrderId('');
                    }
                  }}
                >
                  Find
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <table className="table is-fullwidth">
        <thead>
          <th>Owner</th>
          <th>Name</th>
          <th>Price</th>
          {editing && (
            <th>
              Note{' '}
              <span
                className="icon is-small has-text-info"
                title="Notes are for audit trail purposes only and are not saved as part of the inventory item. Use notes to explain why a change was made."
              >
                <FontAwesomeIcon icon={faCircleQuestion} />
              </span>
            </th>
          )}
          <th></th>
        </thead>
        <tbody>
          {items.valueSeq().map(i => row(i))}
        </tbody>
      </table>
    </div>
  )
}
